"use client";

import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, CheckCircle, XCircle, Clock, AlertCircle, ChevronRight, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { createClient } from '@/lib/supabase/client';

interface AuditLogRow {
  action_type: string;
  outcome: string;
  risk_level: string;
  mfa_status: string;
  resource: string;
  event_timestamp: string;
}

interface ComplianceControl {
  section: string;
  title: string;
  titleBn: string;
  status: 'compliant' | 'non_compliant' | 'in_review' | 'pending';
  score: number;
}

const statusConfig = {
  compliant: { icon: CheckCircle, color: '#00E676', bg: 'bg-green-500/10', border: 'border-green-500/25', label: 'compliant' },
  non_compliant: { icon: XCircle, color: '#FF3B3B', bg: 'bg-red-500/10', border: 'border-red-500/25', label: 'non_compliant' },
  in_review: { icon: Clock, color: '#FFB800', bg: 'bg-yellow-500/10', border: 'border-yellow-500/25', label: 'in_review' },
  pending: { icon: AlertCircle, color: '#6B7A99', bg: 'bg-white/5', border: 'border-cyber-border', label: 'pending' },
};

function deriveStatus(score: number): ComplianceControl['status'] {
  if (score >= 80) return 'compliant';
  if (score >= 50) return 'in_review';
  if (score > 0) return 'non_compliant';
  return 'pending';
}

function calculateComplianceControls(logs: AuditLogRow[]): ComplianceControl[] {
  const total = logs.length;

  // §4 — Data Protection: data action_type with success outcome
  const dataLogs = logs.filter(l => l.action_type === 'data');
  const dataSuccess = dataLogs.filter(l => l.outcome === 'success').length;
  const s4Score = dataLogs.length > 0 ? Math.round((dataSuccess / dataLogs.length) * 100) : (total > 0 ? 50 : 0);

  // §7 — Incident Reporting (24h): threat logs present (any threat logged = reporting active)
  const threatLogs = logs.filter(l => l.action_type === 'threat');
  const recentThreat = threatLogs.filter(l => {
    const ts = new Date(l.event_timestamp).getTime();
    return Date.now() - ts < 7 * 24 * 60 * 60 * 1000; // within last 7 days
  });
  const s7Score = total === 0 ? 0 : threatLogs.length > 0
    ? Math.min(100, 60 + Math.round((recentThreat.length / Math.max(threatLogs.length, 1)) * 40))
    : 30;

  // §11 — Access Control & IAM: auth logs with mfa_status = 'verified' ratio
  const authLogs = logs.filter(l => l.action_type === 'auth');
  const mfaVerified = authLogs.filter(l => l.mfa_status === 'verified').length;
  const mfaFailed = authLogs.filter(l => l.mfa_status === 'failed').length;
  const s11Score = authLogs.length > 0
    ? Math.max(0, Math.round(((mfaVerified - mfaFailed * 2) / authLogs.length) * 100))
    : (total > 0 ? 40 : 0);

  // §14 — Encryption Standards: config logs related to security settings
  const configLogs = logs.filter(l => l.action_type === 'config');
  const securityConfigLogs = configLogs.filter(l =>
    l.resource?.includes('security') || l.resource?.includes('mfa') || l.resource?.includes('api')
  );
  const s14Score = total === 0 ? 0 : configLogs.length > 0
    ? Math.min(100, 60 + Math.round((securityConfigLogs.length / Math.max(configLogs.length, 1)) * 40))
    : 45;

  // §18 — Audit Log Retention (1yr): volume of logs (more logs = better retention evidence)
  const s18Score = total === 0 ? 0
    : total >= 100 ? 95
    : total >= 50 ? 80
    : total >= 20 ? 65
    : total >= 10 ? 50
    : total >= 5 ? 35
    : 20;

  // §21 — Vulnerability Assessment: threat logs with 'blocked' outcome
  const blockedThreats = threatLogs.filter(l => l.outcome === 'blocked').length;
  const s21Score = threatLogs.length > 0
    ? Math.round((blockedThreats / threatLogs.length) * 100)
    : (total > 0 ? 30 : 0);

  // §25 — Third-party Risk Management: admin logs (API key management activity)
  const adminLogs = logs.filter(l => l.action_type === 'admin');
  const s25Score = total === 0 ? 0 : adminLogs.length > 0
    ? Math.min(100, 50 + adminLogs.length * 10)
    : 0;

  // §29 — Cyber Drill Exercises: combined threat + admin activity diversity
  const activityTypes = new Set(logs.map(l => l.action_type)).size;
  const s29Score = total === 0 ? 0
    : activityTypes >= 4 ? 75
    : activityTypes >= 3 ? 55
    : activityTypes >= 2 ? 35
    : 15;

  const controls: ComplianceControl[] = [
    { section: '§4', title: 'Data Protection Measures', titleBn: 'তথ্য সুরক্ষা ব্যবস্থা', score: Math.max(0, Math.min(100, s4Score)), status: 'compliant' },
    { section: '§7', title: 'Incident Reporting (24h)', titleBn: 'ঘটনা রিপোর্টিং (২৪ঘণ্টা)', score: Math.max(0, Math.min(100, s7Score)), status: 'compliant' },
    { section: '§11', title: 'Access Control & IAM', titleBn: 'অ্যাক্সেস নিয়ন্ত্রণ ও আইএএম', score: Math.max(0, Math.min(100, s11Score)), status: 'compliant' },
    { section: '§14', title: 'Encryption Standards', titleBn: 'এনক্রিপশন মান', score: Math.max(0, Math.min(100, s14Score)), status: 'compliant' },
    { section: '§18', title: 'Audit Log Retention (1yr)', titleBn: 'অডিট লগ সংরক্ষণ (১ বছর)', score: Math.max(0, Math.min(100, s18Score)), status: 'compliant' },
    { section: '§21', title: 'Vulnerability Assessment', titleBn: 'দুর্বলতা মূল্যায়ন', score: Math.max(0, Math.min(100, s21Score)), status: 'compliant' },
    { section: '§25', title: 'Third-party Risk Mgmt', titleBn: 'তৃতীয় পক্ষের ঝুঁকি ব্যবস্থাপনা', score: Math.max(0, Math.min(100, s25Score)), status: 'compliant' },
    { section: '§29', title: 'Cyber Drill Exercises', titleBn: 'সাইবার ড্রিল অনুশীলন', score: Math.max(0, Math.min(100, s29Score)), status: 'compliant' },
  ];

  return controls.map(c => ({ ...c, status: deriveStatus(c.score) }));
}

export default function BDCompliancePanel() {
  const { language } = useLanguage();
  const [controls, setControls] = useState<ComplianceControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchAndCalculate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: fetchError } = await supabase
        .from('audit_logs')
        .select('action_type, outcome, risk_level, mfa_status, resource, event_timestamp')
        .order('event_timestamp', { ascending: false })
        .limit(500);

      if (fetchError) {
        console.warn('[BDCompliancePanel] Fetch error:', fetchError.message);
        setError(fetchError.message);
        setLoading(false);
        return;
      }

      const logs: AuditLogRow[] = data || [];
      const calculated = calculateComplianceControls(logs);
      setControls(calculated);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.warn('[BDCompliancePanel] Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAndCalculate();

    // Subscribe to real-time audit_logs changes
    const supabase = createClient();
    const channel = supabase
      .channel('compliance_audit_logs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'audit_logs' }, () => {
        fetchAndCalculate();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAndCalculate]);

  const compliantCount = controls.filter(c => c.status === 'compliant').length;
  const total = controls.length;
  const overallScore = total > 0
    ? Math.round(controls.reduce((sum, c) => sum + c.score, 0) / total)
    : 0;

  return (
    <div className="cyber-card flex flex-col overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-cyber-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <BookOpen size={15} className="text-cyber-blue" />
            <h3 className={`text-sm font-semibold text-white leading-tight ${language === 'bn' ? 'font-bangla text-[13px]' : ''}`}>
              {t(language, 'compliance_panel')}
            </h3>
          </div>
          <button
            onClick={fetchAndCalculate}
            disabled={loading}
            className="text-cyber-muted hover:text-cyber-blue transition-colors disabled:opacity-40"
            title="Refresh compliance data"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Overall Score Bar */}
        <div className="flex items-center gap-3 mt-3">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-cyber-muted font-mono">Overall Compliance</span>
              {loading ? (
                <span className="text-[11px] font-mono text-cyber-muted animate-pulse">—%</span>
              ) : (
                <span className={`text-[11px] font-mono font-bold ${overallScore >= 70 ? 'text-yellow-400' : 'text-cyber-red'}`}>
                  {overallScore}%
                </span>
              )}
            </div>
            <div className="h-1.5 bg-cyber-border rounded-full overflow-hidden">
              {loading ? (
                <div className="h-full w-1/3 rounded-full bg-cyber-border animate-pulse" />
              ) : (
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${overallScore}%`,
                    background: overallScore >= 80 ? '#00E676' : overallScore >= 60 ? '#FFB800' : '#FF3B3B',
                  }}
                />
              )}
            </div>
            {lastUpdated && !loading && (
              <p className="text-[9px] text-cyber-muted font-mono mt-1 opacity-60">
                Live · {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="text-center">
            {loading ? (
              <p className="text-lg font-bold font-mono text-cyber-muted animate-pulse">—/—</p>
            ) : (
              <p className="text-lg font-bold font-mono text-white tabular-nums">{compliantCount}/{total}</p>
            )}
            <p className="text-[9px] text-cyber-muted font-mono">Controls</p>
          </div>
        </div>
      </div>

      {/* Controls List */}
      <div className="flex-1 overflow-y-auto scrollbar-cyber divide-y divide-cyber-border/50">
        {error && (
          <div className="px-4 py-3 text-[11px] text-cyber-red font-mono">
            Failed to load compliance data
          </div>
        )}
        {loading && !error && (
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3 animate-pulse">
                <div className="w-8 h-3 bg-cyber-border rounded" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-cyber-border rounded w-3/4" />
                  <div className="h-1 bg-cyber-border rounded w-1/2" />
                </div>
                <div className="w-16 h-4 bg-cyber-border rounded" />
              </div>
            ))}
          </>
        )}
        {!loading && !error && controls.map((control) => {
          const config = statusConfig[control.status];
          const StatusIcon = config.icon;
          return (
            <div
              key={control.section}
              className="px-4 py-3 hover:bg-white/3 transition-colors cursor-pointer group flex items-center gap-3"
            >
              <span className="text-[11px] font-mono text-cyber-blue font-medium w-8 shrink-0">
                {control.section}
              </span>

              <div className="flex-1 min-w-0">
                <p className={`text-[12px] text-cyber-text font-medium truncate ${language === 'bn' ? 'font-bangla' : ''}`}>
                  {language === 'bn' ? control.titleBn : control.title}
                </p>
                {control.score > 0 && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="flex-1 h-1 bg-cyber-border rounded-full overflow-hidden max-w-[80px]">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${control.score}%`,
                          backgroundColor: config.color,
                          opacity: 0.7,
                        }}
                      />
                    </div>
                    <span className="text-[9px] font-mono text-cyber-muted">{control.score}%</span>
                  </div>
                )}
              </div>

              <div
                className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-[9px] font-mono shrink-0 ${config.bg} ${config.border}`}
                style={{ color: config.color }}
              >
                <StatusIcon size={10} />
                <span className={language === 'bn' ? 'font-bangla text-[9px]' : ''}>
                  {t(language, config.label as any)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-5 py-3 border-t border-cyber-border">
        <button className="w-full flex items-center justify-center gap-1.5 text-[11px] text-cyber-blue hover:text-cyber-blue/80 transition-colors font-medium">
          Full Compliance Report <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}